import {
  Button,
  Flex,
  List,
  ListView,
  Modal,
  Radio,
  Result,
  SearchBar,
  Toast,
} from "antd-mobile";
import React, { useEffect, useMemo, useState } from "react";
import storageX from "../../../utils/storageX";
import {
  getCenterByProjectId,
  subjectTransferQuery,
  transoutSubject,
} from "../../../api/project";
import { debounce } from "lodash";
import { LeftOutline, FileOutline } from "antd-mobile-icons";
import { useHistory } from "react-router-dom";

type TransoutModalProps = {
  visible: boolean;
  projectId: number;
  subjectCode: string;
  onClose: () => void;
};

type CenterItem = {
  id: number;
  code: string;
  title: string;
  type: string;
  value: string;
};

type DoctorItem = {
  code: string;
  name: string;
};

type DoctorData = {
  userCode: string;
  userName: string;
  centerId: number;
  centerName: string;
  totalSubjects: number;
  tenantUserDtos: DoctorItem[];
};

const TransoutModal = (props: TransoutModalProps) => {
  const [visible, setVisible] = useState(false);

  const [centerId, setCenterId] = useState(0);
  const [projectId, setProjectId] = useState(0);
  const [centerSourceOrigin, setCenterSourceOrigin] = useState<CenterItem[]>(
    []
  );
  const [doctorSourceOrigin, setDoctorSourceOrigin] = useState<DoctorItem[]>(
    []
  );
  const [doctorCode, setDoctorCode] = useState("");
  const [user] = useState(() => storageX().user());
  const [keyword, setKeyword] = useState("");

  const [dataSource, setDataSource] = useState(
    () =>
      new ListView.DataSource({
        rowHasChanged: (row1: any, row2: any) => row1.id !== row2.id,
      })
  );
  //受试者名称
  const targetName = sessionStorage.getItem('targetName') || '受试者';
  const d = useHistory()

  const initState = (initDataSource = true) => {
    setCenterId(0);
    setDoctorCode("");
    setDoctorSourceOrigin([]);
    // 重置
    if (initDataSource) {
      setDataSource(dataSource.cloneWithRows(centerSourceOrigin));
    }
  };

  useEffect(() => {
    if (props.visible && props.projectId) {
      // 重置
      initState();
      setProjectId(props.projectId);
      getCenterByProjectId(props.projectId)
        .then((res) => {
          const data = res.data as CenterItem[];
          setCenterSourceOrigin(data);
          setDataSource(dataSource.cloneWithRows(data));
        })
        .catch(() => {});
      setVisible(true);
    } else {
      setVisible(false);
    }
  }, [props.visible, props.projectId]);

  // 获取医生列表
  const fetchUser = async (item: CenterItem) => {
    if (item.id === centerId) {
      return;
    }

    const { code, message, data } = await subjectTransferQuery<DoctorData>(
      user.userCode,
      item.id,
      projectId
    );

    if (code !== 0) {
      Toast.fail(message);
      return;
    }

    const list = [
      { code: "@BACK", name: "返回中心列表" },
      ...data.tenantUserDtos,
    ];

    setKeyword('')
    setCenterId(item.id);
    setDoctorSourceOrigin(list);

    setDataSource(dataSource.cloneWithRows(list));
  };

  //
  const onSearchCenter = (value: string) => {
    initState(false);
    setDataSource(
      dataSource.cloneWithRows(
        centerSourceOrigin.filter(({ title }) => title.indexOf(value) > -1)
      )
    );
  };

  const onSearchDoctor = (value: string) => {
    setDoctorCode("");
    setDataSource(
      dataSource.cloneWithRows(
        doctorSourceOrigin.filter(({ name, code }) => {
          return name.indexOf(value) > -1 || code === "@BACK";
        })
      )
    );
  };

  //
  const centerDebounce = useMemo(
    () => debounce((value) => onSearchCenter(value), 500),
    [centerSourceOrigin]
  );
  //
  const doctorDebounce = useMemo(
    () => debounce((value) => onSearchDoctor(value), 500),
    [doctorSourceOrigin]
  );
  // 用户输入搜索时
  const onChange = (value: string) => {
    setKeyword(value);
    if (centerId === 0) {
      centerDebounce(value);
    } else {
      doctorDebounce(value);
    }
  };

  const onConfirm = async () => {
    const { code, message } = await transoutSubject<any>({
      projectId,
      centerId,
      transferCode: user.userCode,
      recipientCode: doctorCode,
      totalSubjects: 1,
      subjectCodes: [props.subjectCode],
    });

    if (code === 0) {
      onClose();
      Toast.success(`${targetName}已转移`, 3, ()=> {
        // @ts-ignore
        d.goBack()
      });
    } else {
      Toast.fail(message);
    }
  };

  const onClose = () => {
    props.onClose();
    setVisible(false);
  };

  // 渲染中心列表
  const renderCenter = (item: CenterItem) => {
    return <List.Item onClick={() => fetchUser(item)}>{item.title}</List.Item>;
  };

  // 渲染医生列表
  const renderDoctor = (item: DoctorItem) => {
    if (item.code === "@BACK") {
      return (
        <List.Item
          onClick={() => {
            initState();
          }}
        >
          <LeftOutline color="#999" />
          {item.name}
        </List.Item>
      );
    }
    return (
      <Radio.RadioItem
        checked={doctorCode === item.code}
        onChange={() => {
          setDoctorCode(item.code);
        }}
      >
        {item.name}
      </Radio.RadioItem>
    );
  };
  // 渲染列表
  const renderRow = (item: any) => {
    return centerId === 0 ? renderCenter(item) : renderDoctor(item);
  };

  //
  const isEmpty = () => {
    const ds = dataSource.rowIdentities[0] ?? [];
    return ds.length === 0 || (ds.length === 1 && centerId > 0);
  };

  return (
    <Modal
      popup
      closable
      transparent
      maskClosable
      title={`转移${targetName}`}
      visible={visible}
      onClose={onClose}
      animationType="slide-up"
    >
      <Flex direction="column">
        <Flex.Item style={{ width: "100%" }}>
          <SearchBar
            value={keyword}
            onChange={onChange}
            placeholder={centerId === 0 ? "请输入中心名称" : "请输入医生名称"}
          />
        </Flex.Item>
        <Flex.Item style={{ width: "100%" }}>
          {/* 处理中心列表 */}
          {isEmpty() ? (
            <Result
              style={{ height: 300 }}
              img={<FileOutline color="#eee" fontSize={48} />}
              message="暂无数据"
            />
          ) : (
            <ListView
              useBodyScroll={false}
              dataSource={dataSource}
              style={{ height: 300 }}
              renderRow={renderRow}
            ></ListView>
          )}
        </Flex.Item>
        <Flex.Item style={{ width: "100%", marginTop: 10 }}>
          <Flex>
            <Flex.Item flex={1} style={{ paddingRight: 10 }}>
              <Button onClick={onClose}>取消</Button>
            </Flex.Item>
            <Flex.Item flex={1} style={{ paddingLeft: 10 }}>
              <Button disabled={doctorCode.length === 0} onClick={onConfirm} type="primary">
                确定
              </Button>
            </Flex.Item>
          </Flex>
        </Flex.Item>
      </Flex>
    </Modal>
  );
};

export default TransoutModal;
